import '@/styles/chatbot-layout.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { SimpleMenu } from '@/components/MenuGrid';
import { useLocale } from '@/context/LocaleContext';
import { useChatbotHistory, useChatbotMutations } from '@/hooks/useChatbot';
import { ensureMarkedReady } from '@/lib/chatbotMarkdown';
import type { AuthUser, PlayerData } from '@/vite-env.d';
import { ChatErrorMessage, ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';

interface AppShellContext {
  user: AuthUser;
  player: PlayerData | null;
}

export function ChatbotPage() {
  const { t } = useLocale();
  const { user, player } = useOutletContext<AppShellContext>();
  const charId = user.charId;
  const charName = player?.CharName16 ?? 'Usuario';
  const refObjId = player?.RefObjID ?? 14875;

  const { data: history = [], isLoading, isError } = useChatbotHistory(charId);
  const { send } = useChatbotMutations(charId);

  const [pendingUser, setPendingUser] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    void ensureMarkedReady().catch(() => undefined);
  }, []);

  const displayMessages = useMemo(() => {
    const base = [...history];
    if (pendingUser) {
      base.push({ role: 'user', content: pendingUser });
    }
    return base;
  }, [history, pendingUser]);

  const isWaiting = send.isPending;
  const showWelcome = !isLoading && history.length === 0 && !pendingUser && !isError;

  const handleSend = useCallback(
    async (message: string) => {
      setErrorText(null);
      setPendingUser(message);
      try {
        await send.mutateAsync(message);
        setPendingUser(null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('chat_error_connection');
        setErrorText(msg);
      }
    },
    [send, t],
  );

  return (
    <div className="container-ingame fade-in rai-page">
      <div className="chatbot-container">
        <SimpleMenu title={t('r_ia')} />

        {isLoading ? (
          <div className="chat-messages" id="chat-messages">
            <div className="message assistant">
              <div className="message-content">{t('loading_data')}</div>
            </div>
          </div>
        ) : isError ? (
          <div className="chat-messages" id="chat-messages">
            <ChatErrorMessage text={t('chat_load_error')} />
          </div>
        ) : (
          <ChatMessageList
            messages={displayMessages}
            charName={charName}
            refObjId={refObjId}
            welcomeHtml={t('chat_welcome_message')}
            questionHtml={t('chat_welcome_question')}
            isWaiting={isWaiting}
            showWelcome={showWelcome}
            errorText={errorText}
          />
        )}

        <ChatInput disabled={isLoading || isError || isWaiting} onSend={handleSend} />
      </div>
    </div>
  );
}

export default ChatbotPage;
